import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';

/**
 * NotFound — catch-all 404 page.
 *
 * SEO contract:
 *  • noindex, nofollow — prevents Google from treating soft-404s as indexable
 *    pages. This fixes "Duplicate without user-selected canonical" in GSC for
 *    all the old WordPress flat-slug URLs that are still in Google's index
 *    (e.g. /cali/, /foodgasm/, /best-napa-valley-wine/).
 *  • No <link rel="canonical"> — there is no canonical URL for a page that
 *    doesn't exist.
 */
const NotFound = () => {
  const { pathname } = useLocation();

  return (
    <>
      <Helmet>
        <title>Page Not Found | Winery Finder App</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>

      <div
        className="min-h-[60vh] flex flex-col items-center justify-center px-6 py-24 text-center"
        data-testid="not-found-page"
      >
        <p className="text-8xl font-bold text-wine-200 select-none mb-4">404</p>
        <h1 className="text-2xl font-semibold text-gray-900 mb-3">
          Page not found
        </h1>
        <p className="text-gray-500 max-w-sm mb-8 text-sm leading-relaxed">
          The URL <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs text-gray-700 break-all">{pathname}</code> doesn't
          exist on this site. It may have moved or been removed.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-wine-600 text-white text-sm font-semibold hover:bg-wine-700 transition"
            data-testid="not-found-home-link"
          >
            Go to homepage
          </Link>
          <Link
            to="/wineries"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gray-300 text-gray-700 text-sm font-semibold hover:border-wine-400 hover:text-wine-700 transition"
            data-testid="not-found-wineries-link"
          >
            Browse wineries
          </Link>
        </div>
      </div>
    </>
  );
};

export default NotFound;
